import { useEffect, useState } from "react";
import { api, ApiError, type OhjeVideo } from "./api";
import { kaannos, kieli, t, virheTeksti } from "./kieli";
import type { KaannosAvain } from "./kaannokset";
import { Markdown } from "./Testaus";

/* ═══ OHJEET (T481, Toni 11.10.2026 «Ohjeet-osio GO, pekilon työnjohtajat työlajin mukaan») ═══
   Pysyvä roolikohtainen ohje jokaisessa instanssissa (Testaus-näkymä on vain demossa ja vain testaajille). Palvelin
   valitsee roolin tunnuksesta — kenttätilillä työlajista, joten pekilon työnjohtaja saa työnjohtajan ohjeen eikä
   hitsaajan. Sisältö on repossa ohjeet/<rooli>.<kieli>.md; videot tulevat omalta palvelimelta kirjautuneelle.
   Pääkäyttäjä voi katsoa minkä tahansa roolin ohjetta (valitsin), muut näkevät vain omansa. */

type Ohje = Awaited<ReturnType<typeof api.ohjeet>>;

export default function Ohjeet() {
  const [ohje, setOhje] = useState<Ohje | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  // 12.10.2026: kun tunnukselle sopii useampi ohje (DPU punch / linjatarkastaja, suunnittelija projektipäällikkö /
  // työnsuunnittelija), valinta muistetaan selaimeen, ei kantaan. Palvelin hylkää valinnan, joka ei kuulu tunnukselle.
  const [rooli, setRooliTila] = useState<string | undefined>(() => { try { return window.localStorage.getItem("sampo.ohjerooli") ?? undefined; } catch { return undefined; } });
  const setRooli = (r: string) => { setRooliTila(r); try { window.localStorage.setItem("sampo.ohjerooli", r); } catch { /* ei tallennusta */ } };
  useEffect(() => {
    api.ohjeet(kieli(), rooli).then((o) => { setOhje(o); setVirhe(null); })
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("ohjeet.haku_virhe")));
  }, [rooli]);
  return (
    <>
      <header className="head tuloste-tyokalut">
        <div className="eyebrow">{t("ohjeet.yla")}</div>
        <div className="head__rivi">
          <h1>{t("ohjeet.otsikko")}</h1>
          <div className="head__ohjaus">
            {ohje?.roolit && (
              <label className="ohjeet__rooli">
                <span className="dim">{t("ohjeet.rooli")}</span>
                <select value={ohje.rooli} onChange={(e) => setRooli(e.target.value)} data-ohjeet-roolivalinta="">
                  {ohje.roolit.map((r) => (
                    <option key={r} value={r}>
                      {t(`ohjeet.rooli.${r}` as KaannosAvain)}{ohje.kirjoitetut && !ohje.kirjoitetut.includes(r) ? ` — ${t("ohjeet.kirjoittamatta")}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {ohje?.testaus && <a className="nappi nappi--kevyt" href="#/testaus">{t("ohjeet.testausohje")}</a>}
            <button type="button" className="nappi nappi--kevyt" onClick={() => window.print()} disabled={!ohje?.sisalto}>
              {t("testaus.tulosta")}
            </button>
          </div>
        </div>
      </header>
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      {!ohje && !virhe && <p className="dim">{t("yleinen.ladataan")}</p>}
      {ohje && !ohje.sisalto && (
        <p className="panel dim" data-ohjeet-rooli={ohje.rooli} data-ohjeet-tyhja="">
          {t("ohjeet.ei_ohjetta", { rooli: t(`ohjeet.rooli.${ohje.rooli}` as KaannosAvain) })}
        </p>
      )}
      {ohje?.sisalto && (
        <article className="panel testausohje tuloste" data-ohjeet-rooli={ohje.rooli} data-ohjeet-kieli={ohje.kieli}
                 data-ohjeet-nakyy={ohje.nakyy.join(",")} data-ohjeet-ei-nay={ohje.ei_nay.join(",")}>
          {/* Kieliversio puuttuu (11.10.2026): sanotaan lukijan kielellä, ettei kielivaihto näytä rikkinäiseltä. */}
          {/* 12.10.2026: kun ohjetta ei ole lukijan kielellä tai käännös on vanhentunut, huomautus lukijan kielellä ja englanniksi. */}
          {ohje.kieli !== kieli() && <p className="huom" data-ohje-kieliversio-puuttuu={kieli()} data-ohje-vanhentunut={(ohje.vanhentuneet ?? []).join(",")}>
            {t("ohjeet.ei_kielellasi")}{kieli() !== "en" && kaannos("ohjeet.ei_kielellasi", "en") !== t("ohjeet.ei_kielellasi") ? ` · ${kaannos("ohjeet.ei_kielellasi", "en")}` : ""}
            {" "}{t("ohjeet.vain_kielella", { kieli: t(`kieli.nimi.${ohje.kieli}` as KaannosAvain) })}</p>}
          {ohje.otsikko && <h1>{ohje.otsikko}</h1>}
          {ohje.videot.length > 0 && (
            <section className="ohjeet__videot" data-ohjeet-videot={ohje.videot.length}>
              {ohje.videot.map((v) => <Video key={v.tunnus} v={v} />)}
            </section>
          )}
          <Markdown teksti={ohje.sisalto} />
        </article>
      )}
      {/* T507 (Toni 23.10.2026 klo 06.45): opetussarja näkyy roolista riippumatta; palvelin on jo jättänyt pois ostamattomien
          moduulien osat. Ryhmät sarjan vaiheista luettelon järjestyksessä. */}
      {ohje && (ohje.sarja?.length ?? 0) > 0 && (
        <section className="panel ohjeet__sarja" data-ohjeet-sarja={ohje.sarja!.length}>
          <h2>{t("ohjeet.sarja")}</h2>
          {vaiheittain(ohje.sarja!).map(([vaihe, rivit]) => (
            <div key={vaihe} className="ohjeet__sarjavaihe">
              {vaihe && <h3>{vaihe}</h3>}
              <div className="ohjeet__videot">{rivit.map((v) => <Video key={v.tunnus} v={v} />)}</div>
            </div>
          ))}
        </section>
      )}
    </>
  );
}

function vaiheittain(rivit: OhjeVideo[]): [string, OhjeVideo[]][] {
  const ryhmat: [string, OhjeVideo[]][] = [];
  for (const v of rivit) {
    const vaihe = v.vaihe ?? "";
    const viimeinen = ryhmat[ryhmat.length - 1];
    if (viimeinen && viimeinen[0] === vaihe) viimeinen[1].push(v); else ryhmat.push([vaihe, [v]]);
  }
  return ryhmat;
}

function Video({ v }: { v: OhjeVideo }) {
  return (
    <figure className={"ohjeet__video" + (v.pysty ? " ohjeet__video--pysty" : "")}>
      <video controls preload="metadata" src={`/api/ohjeet/video/${v.tunnus}?kieli=${v.versio}`} data-ohjeet-video={v.tunnus}>
        {v.tekstitys.map((kl) => (
          <track key={kl} kind="subtitles" srcLang={kl} label={kl.toUpperCase()}
                 src={`/api/ohjeet/video/${v.tunnus}/tekstitys/${kl}`} default={kl === v.oletusraita}
                 data-raita-oletus={kl === v.oletusraita ? "1" : "0"} />
        ))}
      </video>
      {/* Toni 11.10.2026: jos ohjeen kielellä ei ole omaa versiota, näytetään toinen ja kieli sanotaan («(FI)»). */}
      <figcaption>{v.otsikko}{v.merkinta ? ` (${v.merkinta.toUpperCase()})` : ""}{v.kesto ? ` · ${v.kesto}` : ""}</figcaption>
    </figure>
  );
}
